import React, { Component } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

export default class DeadlineSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      states: [],
      counties: [],
      selectedState: '',
      selectedCounty: '',
      stateAbbreviations: {},
      loading: false,
      error: '',
      redirect: false,
    };
  }

  componentDidMount() {
    this.fetchStates();
  }

  fetchStates = () => {
    this.setState({ loading: true });
    const ajax_wrapper = (method, url, data, callback) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            callback(JSON.parse(xhr.responseText));
          } else {
            this.setState({
              error: 'Failed to fetch states',
              loading: false,
            });
          }
        }
      };
      xhr.send(data ? JSON.stringify(data) : null);
    };

    ajax_wrapper(
      'GET',
      '/api/property_tax_deadline/states/',
      null,
      (response) => {
        this.setState({
          states: response.states,
          stateAbbreviations: response.state_abbreviations || {},
          loading: false,
        });
      }
    );
  };

  fetchCounties = (state) => {
    this.setState({ loading: true, counties: [], selectedCounty: '' });
    const ajax_wrapper = (method, url, data, callback) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            callback(JSON.parse(xhr.responseText));
          } else {
            this.setState({
              error: 'Failed to fetch counties',
              loading: false,
            });
          }
        }
      };
      xhr.send(data ? JSON.stringify(data) : null);
    };

    ajax_wrapper(
      'GET',
      `/api/property_tax_deadline/counties/?state=${encodeURIComponent(state)}`,
      null,
      (response) => {
        this.setState({
          counties: response.counties,
          loading: false,
        });
      }
    );
  };

  handleStateChange = (e) => {
    const selectedState = e.target.value;
    this.setState({ selectedState });
    if (selectedState) {
      this.fetchCounties(selectedState);
    }
  };

  handleCountyChange = (e) => {
    this.setState({ selectedCounty: e.target.value });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { selectedState, selectedCounty } = this.state;
    if (selectedState && selectedCounty) {
      this.setState({
        redirect: true,
      });
    } else {
      this.setState({
        error: 'Please select both state and county',
      });
    }
  };

  render() {
    const {
      states,
      counties,
      selectedState,
      selectedCounty,
      stateAbbreviations,
      loading,
      error,
      redirect,
    } = this.state;

    if (redirect) {
      const stateAbbr = stateAbbreviations[selectedState] || selectedState;
      
      const isDC = selectedState === 'District of Columbia' || stateAbbr === 'DC';
      
      if (isDC) {
        return <Navigate to={`/property_tax_deadline/DC/DC`} />;
      } else {
        const state = encodeURIComponent(stateAbbr.trim());
        
        // Keep the county name as is, but remove "County" suffix for URL clarity
        let countyName = selectedCounty.trim();
        
        // Only remove "County" suffix for cleaner URLs, preserve other special suffixes
        if (countyName.endsWith(' County')) {
          countyName = countyName.replace(/\s+County$/, '');
        }
        
        // Replace spaces with underscores for cleaner URLs
        const countyForUrl = countyName.replace(/\s+/g, '_');
        
        return <Navigate to={`/property_tax_deadline/${state}/${countyForUrl}`} />;
      }
    }

    const selectStyles = {
      height: '60px',
      fontSize: '1.1rem',
      width: '100%',
      minWidth: '200px',
      '& .MuiSelect-select': {
        paddingTop: '15px',
        paddingBottom: '15px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
        borderWidth: '2px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.87)',
        borderWidth: '2px',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1976d2',
        borderWidth: '2px',
      },
    };

    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Helmet>
          <title>Property Tax Appeal Deadline Search | YourHomeBase</title>
          <meta
            name="description"
            content="Find property tax appeal deadlines for your county. Know when to file your property tax appeal with accurate deadline information."
          />
        </Helmet>

        <Card elevation={4} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box 
            sx={{ 
              bgcolor: '#4c5fd5', 
              color: 'white', 
              py: 3, 
              px: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: 'linear-gradient(90deg, #4c5fd5 0%, #5a6feb 100%)',
            }}
          >
            <CalendarTodayIcon fontSize="large" />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Property Tax Appeal Deadline Search
            </Typography>
          </Box>
          
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" paragraph align="center" color="text.secondary" sx={{ mb: 4 }}>
              Find out when you need to file your property tax appeal in your county.
            </Typography>
            
            {error && (
              <Box sx={{ 
                mt: 2, 
                mb: 3, 
                p: 2, 
                bgcolor: 'error.light',
                color: 'error.dark',
                borderRadius: 1
              }}>
                <Typography fontWeight="medium">{error}</Typography>
              </Box>
            )}
            
            <form onSubmit={this.handleSubmit}>
              <Grid container spacing={4} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="state-select-label" sx={{ fontSize: '1.1rem' }}>State</InputLabel>
                    <Select
                      labelId="state-select-label"
                      id="state-select"
                      value={selectedState}
                      label="State"
                      onChange={this.handleStateChange}
                      disabled={loading || states.length === 0}
                      sx={selectStyles}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {states.map((state) => (
                        <MenuItem key={state} value={state} sx={{ fontSize: '1.1rem', py: 1 }}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="county-select-label" sx={{ fontSize: '1.1rem' }}>County</InputLabel>
                    <Select
                      labelId="county-select-label"
                      id="county-select"
                      value={selectedCounty}
                      label="County"
                      onChange={this.handleCountyChange}
                      disabled={loading || !selectedState || counties.length === 0}
                      sx={selectStyles}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {counties.map((county) => (
                        <MenuItem key={county} value={county} sx={{ fontSize: '1.1rem', py: 1 }}>
                          {county}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading || !selectedState || !selectedCounty}
                    sx={{ 
                      py: 2, 
                      height: '60px',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      boxShadow: 3,
                      bgcolor: '#4c5fd5',
                      '&:hover': {
                        bgcolor: '#3f51b5',
                        boxShadow: 5,
                      }
                    }}
                    startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
                  >
                    {loading ? 'Loading...' : 'Find Deadline'}
                  </Button>
                </Grid>
              </Grid>
            </form>
            
            <Divider sx={{ my: 4 }} />
            
            <Box 
              sx={{ 
                bgcolor: 'rgba(76, 95, 213, 0.05)', 
                p: 3, 
                borderRadius: 2,
                border: '1px solid rgba(76, 95, 213, 0.2)',
              }}
            >
              <Typography variant="body1" align="center">
                Want to know if your property is overassessed? Check our{' '}
                <Link 
                  to="/property_tax_calculator"
                  style={{ 
                    color: '#4c5fd5', 
                    fontWeight: 'bold',
                    textDecoration: 'none',
                  }}
                >
                  Property Tax Calculator
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }
} 